import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';

import { MODO_PARTICIPACAO_ADVOGADO } from '@/dominio/ModoParticipacaoAdvogado';

import '../../css/certidao.css';
import Configuracao from '../../util/Configuracao';
import StringUtil from '../../util/StringUtil';
import AutoCompleteRedator from '../AutocompleteRedator';
import ReactComponent from '../ReactComponent';
import ModalConfirmacao from '../sessao/ModalConfirmacao';
import TextFieldSecretaria from '../TextFieldSecretaria';
import AUTO_TEXTO_CERTIDAO from './AutoTextoCertidao';
import CamposAutoTexto from './CamposAutoTexto';
import Certidao from './Certidao';

class RascunhoCertidao extends ReactComponent {
    refCampoRascunho = React.createRef();

    tipoJulgamento;

    larguraTela;

    modalConfirmacaoDesbloqueioOpen = false;

    carregarCertidao = async () => {
        if (this.registro && (this.registro.rascunhoEmEdicao === false || !this.registro.rascunhoCertidao)) {
            await this.salaSessaoStore.carregarRascunhoCertidao(this.registro);
        }
    };

    incluirComplementoAdvogado = (adv, complemento) => {
        if (!adv?.nome) return;

        const nomeBusca = adv.nome.toString().toLowerCase().trim();

        const advogado = Certidao.advogados(this.registro).find(
            (a) => a.texto && nomeBusca.includes(a.texto.toLowerCase().trim())
        );

        if (!advogado) return;

        const modo = advogado.modoParticipacao;
        let textoFinal = complemento;

        const regrasPresenca = {
            [MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo]:
                AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA_TELEPRESENCIAL.texto,
            [MODO_PARTICIPACAO_ADVOGADO.VIRTUAL.codigo]: AUTO_TEXTO_CERTIDAO.REGISTRO_PARTICIPACAO_VIRTUAL.texto
        };

        const regrasSustentacao = {
            [MODO_PARTICIPACAO_ADVOGADO.TELEPRESENCIAL.codigo]:
                AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL_TELEPRESENCIAL.texto,
            [MODO_PARTICIPACAO_ADVOGADO.SUSTENTACAO_ORAL_ELETRONICA.codigo]:
                AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL_ELETRONICA.texto,
            [MODO_PARTICIPACAO_ADVOGADO.PEDIDO_DESTAQUE.codigo]: AUTO_TEXTO_CERTIDAO.PEDIDO_DESTAQUE.texto
        };

        if (complemento === AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA.texto) {
            textoFinal = regrasPresenca[modo] || complemento;
        } else if (complemento === AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL.texto) {
            textoFinal = regrasSustentacao[modo] || complemento;
        }

        this.incluirObservacaoRascunho(this.replaceHashTags(textoFinal, advogado));
    };

    incluirComplementoConteudo = (magistrado, complemento) => {
        if (magistrado) {
            this.incluirObservacaoRascunho(
                this.gerarTratamento(magistrado) + ' ' + magistrado.nome.trim() + complemento
            );
        }
    };

    incluirComplementoConteudoMagistradoAoFinal = (magistrado, complemento) => {
        if (magistrado) {
            this.incluirObservacaoRascunho(
                complemento + this.gerarTratamento(magistrado) + ' ' + magistrado.nome.trim()
            );
        }
    };

    callbackAutoComplete = async (campo, option) => {
        const valor = option?.valor;
        this.registro.rascunhoEmEdicao = true;
        this.callbackAutoCompleteVotos(campo, valor);
        this.callbackAutoCompleteAdvogados(campo, valor);
        await this.callbackAutoCompleteMagistrados(campo, valor);
        this.salaSessaoStore.salvarRascunhoCertidao(this.registro, this.conteudoCertidao);
    };

    callbackAutoCompleteVotos = (campo, valor) => {
        switch (campo) {
            case AUTO_TEXTO_CERTIDAO.VOTO_VENCIDO.id:
                this.incluirComplementoConteudoMagistradoAoFinal(valor, AUTO_TEXTO_CERTIDAO.VOTO_VENCIDO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_VENCIDO.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_VENCIDO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_CONVERGENTE.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.JUNTADA_VOTO_CONVERGENTE.texto);
                break;
            default:
                break;
        }
    };

    callbackAutoCompleteAdvogados = (campo, valor) => {
        switch (campo) {
            case AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA.id:
                this.incluirComplementoAdvogado(valor, AUTO_TEXTO_CERTIDAO.REGISTRO_PRESENCA.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL.id:
                this.incluirComplementoAdvogado(valor, AUTO_TEXTO_CERTIDAO.SUSTENTACAO_ORAL.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.JUNTADA_PROCURACAO.id:
                this.incluirComplementoJuntadaProcuracao(valor);
                break;
            default:
                break;
        }
    };

    changeRascunhoCertidao = (texto) => {
        this.registro.rascunhoCertidao = StringUtil.tratarCaracteresNaoLatin1(texto);
        this.registro.rascunhoEmEdicao = true;
    };

    callbackCancelar = () => {
        this.modalConfirmacaoDesbloqueioOpen = false;
    };

    confirmarDesbloqueioComObservacaoPendente = () => {
        this.modalConfirmacaoDesbloqueioOpen = false;
        this.desbloquearAssinatura();
    };

    constructor(props) {
        super(props);

        makeObservable(this, {
            tipoJulgamento: observable,
            larguraTela: observable,
            modalConfirmacaoDesbloqueioOpen: observable,
            registro: computed,
            conteudoCertidao: computed,
            magistrados: computed,
            processo: computed,
            edicaoRascunhoDesabilitada: computed,
            isLarguraTelaMenor: computed,
            alturaRascunhoCertidao: computed,
            calculaLarguraTela: action,
            carregarCertidao: action,
            callbackAutoComplete: action,
            callbackAutoCompleteVotos: action,
            callbackAutoCompleteAdvogados: action,
            changeRascunhoCertidao: action,
            callbackCancelar: action,
            confirmarDesbloqueioComObservacaoPendente: action,
            incluirObservacaoRascunho: action,
            refCampoRascunho: false,
            componentDidMount: false,
            incluirComplementoAdvogado: false,
            incluirComplementoConteudo: false,
            incluirComplementoConteudoMagistradoAoFinal: false,
            getQuantidadeObservacoes: false,
            gerarTratamento: false,
            callbackAutoCompleteMagistrados: false,
            replaceHashTags: false,
            incluirComplementoJuntadaProcuracao: false,
            adicionarObservacao: false,
            renderModalConfirmaDesbloqueio: false,
            render: false,
            renderRascunho: false,
            renderRascunhoInputProps: false,
            renderAutoTextos: false
        });

        this.createReaction(
            reaction(
                () => this.registro,
                (registro) => {
                    if (registro) {
                        this.carregarCertidao();
                    }
                }
            )
        );
    }

    get registro() {
        return this.observableProps.registro;
    }

    get conteudoCertidao() {
        return this.registro.rascunhoCertidao ?? '';
    }

    calculaLarguraTela() {
        this.larguraTela = window.innerWidth;
    }

    componentDidMount() {
        this.carregarCertidao();
        this.calculaLarguraTela();
    }

    get magistrados() {
        if (this.registro.processo.origem === Configuracao.ORIGEM_PJE) {
            return this.dominioStore.opcoesMagistradosComposicao(this.registro);
        }
        return this.dominioStore.opcoesMagistradosAutoComplete(this.registro);
    }

    get processo() {
        return this.registro.processo;
    }

    get edicaoRascunhoDesabilitada() {
        return !!this.registro?.certidaoGerada;
    }

    get isLarguraTelaMenor() {
        return this.larguraTela <= 1600;
    }

    getQuantidadeObservacoes() {
        return this.salaSessaoStore.getQuantidadeObservacoes(this.conteudoCertidao);
    }

    gerarTratamento(magistrado) {
        return this.salaSessaoStore.pronomeTratamentoComCargo(magistrado);
    }

    callbackAutoCompleteMagistrados(campo, valor) {
        switch (campo) {
            case AUTO_TEXTO_CERTIDAO.RESSALVA_ENTENDIMENTO.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.RESSALVA_ENTENDIMENTO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.RESSALVA_FUNDAMENTACAO.id:
                this.incluirComplementoConteudo(valor, AUTO_TEXTO_CERTIDAO.RESSALVA_FUNDAMENTACAO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.REGISTRO_IMPEDIMENTO.id:
                this.incluirComplementoConteudoMagistradoAoFinal(valor, AUTO_TEXTO_CERTIDAO.REGISTRO_IMPEDIMENTO.texto);
                break;
            case AUTO_TEXTO_CERTIDAO.REGISTRO_AUSENCIA.id:
                this.incluirComplementoConteudoMagistradoAoFinal(valor, AUTO_TEXTO_CERTIDAO.REGISTRO_AUSENCIA.texto);
                break;
            default:
                break;
        }
    }

    replaceHashTags(texto, advogado) {
        const turma = this.usuarioStore.usuario.descricaoLocalizacao;

        return texto
            .replaceAll('#ADV_GEN2', () => (advogado.genero === 'F' ? 'a' : 'o'))
            .replaceAll('#ADV_CAP_GEN2', () => (advogado.genero === 'F' ? 'a' : 'o'))
            .replaceAll('#ADV_GEN', () => (advogado.genero === 'F' ? 'a' : ''))
            .replaceAll('#NOME_ADV', () => (advogado.nome ? advogado.nome.trim() : ''))
            .replaceAll('#DENOMINACAO_PARTE', () => advogado.denominacaoDaParte || '')
            .replaceAll('#NOME_PARTE', () => (advogado.nomeDaParte ? advogado.nomeDaParte.trim() : ''))
            .replaceAll('#TURMA', () => turma);
    }

    incluirComplementoJuntadaProcuracao(valor) {
        this.incluirComplementoAdvogado(
            valor,
            this.salaSessaoStore.isProcessoPje(this.registro)
                ? AUTO_TEXTO_CERTIDAO.JUNTADA_PROCURACAO_PJE.texto
                : AUTO_TEXTO_CERTIDAO.JUNTADA_PROCURACAO.texto
        );
    }

    adicionarObservacao(textoBase, conteudo) {
        const texto = textoBase || '';

        if (texto.includes(conteudo)) {
            return texto;
        }
        const textoRenumerado = this.salaSessaoStore.renumerarObservacoes(texto);
        const numeroObservacao = this.salaSessaoStore.getQuantidadeObservacoes(texto) + 1;
        return textoRenumerado + '\nObservação ' + numeroObservacao + ': ' + conteudo + '.\n';
    }

    incluirObservacaoRascunho(conteudo) {
        const rascunhoAntigo = this.registro.rascunhoCertidao || '';
        const novoRascunho = this.adicionarObservacao(rascunhoAntigo, conteudo);

        if (novoRascunho === rascunhoAntigo) {
            this.mensagemStore.abrirMensagemAlerta('Observação já presente no texto.');
            return rascunhoAntigo;
        }

        const isAindaEditandoRascunho = this.observableProps.textoAtual === undefined && !this.registro.certidaoGerada;

        if (isAindaEditandoRascunho) {
            runInAction(() => {
                this.registro.rascunhoCertidao = novoRascunho;
                this.registro.rascunhoEmEdicao = true;
            });
        }

        if (this.props.onChangeTexto) {
            this.props.onChangeTexto(novoRascunho);
        }

        return novoRascunho;
    }

    get alturaRascunhoCertidao() {
        const alturaParaTelasComMaiorLargura =
            this.store.filtros.filtrosComValor.length > 0 ? 'calc(100vh - 750px)' : 'calc(100vh - 700px)';
        return this.isLarguraTelaMenor ? 'calc(100vh - 750px)' : alturaParaTelasComMaiorLargura;
    }

    renderModalConfirmaDesbloqueio() {
        return this.registro?.certidaoJulgamentoAssinada ? (
            <ModalConfirmacao
                callbackCancelar={this.callbackCancelar}
                callbackConfirmar={this.confirmarDesbloqueioComObservacaoPendente}
                open={this.modalConfirmacaoDesbloqueioOpen}
                titulo={'Desbloquear assinatura?'}
            >
                <Typography>Esta alteração vai desbloquear a assinatura. Confirma?</Typography>
            </ModalConfirmacao>
        ) : null;
    }

    render() {
        if (!this.registro) {
            return null;
        }
        return (
            <>
                <Box
                    sx={{
                        pt: 1,
                        opacity: this.registro.certidaoGerada ? '0.7' : '1'
                    }}
                >
                    {this.renderRascunho()}
                </Box>
                <Box>{this.renderAutoTextos()}</Box>
            </>
        );
    }
    renderRascunho() {
        return (
            <TextFieldSecretaria
                desativarTimeout={false}
                disabled={this.edicaoRascunhoDesabilitada}
                inputProps={this.renderRascunhoInputProps()}
                inputRef={this.refCampoRascunho}
                label="Rascunho da certidão"
                multiline={true}
                onChange={this.changeRascunhoCertidao}
                salvar={this.salaSessaoStore.salvarRascunhoCertidao.bind(this, this.registro)}
                spellCheck={true}
                tempoTimeout={4000}
                value={this.conteudoCertidao}
            />
        );
    }
    renderRascunhoInputProps() {
        return this.isPreSessao
            ? {
                  'data-sentry-mask': true,
                  style: {
                      height:
                          this.observableProps.alturaRascunho && Number(this.observableProps.alturaRascunho) > 0
                              ? `${this.observableProps.alturaRascunho}px`
                              : 'auto',
                      textAlign: 'justify',
                      fontFamily: 'sans-serif'
                  }
              }
            : {
                  'data-sentry-mask': true,
                  style: {
                      height: this.alturaRascunhoCertidao,
                      textAlign: 'justify',
                      fontFamily: 'sans-serif'
                  }
              };
    }

    renderAutoTextos() {
        if (!this.isSalaSessao) {
            return null;
        }

        return (
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <CamposAutoTexto
                    advogados={Certidao.advogados(this.registro)}
                    callbackAutoComplete={this.callbackAutoComplete}
                    magistrados={this.magistrados}
                    registro={this.registro}
                />
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: '3px 0px',
                        flexGrow: 1,
                        width: '80px'
                    }}
                >
                    <AutoCompleteRedator
                        callbackDesbloqueio={() => {
                            if (this.registro.certidaoJulgamentoAssinada) {
                                runInAction(() => (this.registro.certidaoJulgamentoAssinada = false));
                            }
                        }}
                        onChangeTexto={action((novoTexto) => {
                            this.changeRascunhoCertidao(novoTexto);
                        })}
                        registro={this.registro}
                        textoAtual={this.conteudoCertidao}
                    />
                    {this.renderModalConfirmaDesbloqueio()}
                </div>
            </div>
        );
    }
}

export default inject('stores')(observer(RascunhoCertidao));
